import { formatSince, isRecent, TICKER_SUFFIX } from '../../lib/utils/formatSince'

const REFRESH_MS = 60_000

/**
 * Hydrates the relative dates the worker stamps on the page:
 *
 * - `[ck-dayssince-spill]`   incident rows   → "8 hours ago", "3 days ago", "Apr 20, 2025"
 * - `[ck-dayssince-ticker]`  sticky ticker   → "8 hours Since Last Incident" (the worker already
 *                              wrote this text, so on load nothing visibly changes)
 *
 * Each element carries `ck-dayssince` (calendar day) and, when the CMS has one,
 * `ck-dayssince-iso` (full timestamp). Anything under 24 hours old is shown by the hour and
 * kept fresh while the page stays open.
 */
export default function daysSince() {
  const targets: { element: Element; suffix?: string }[] = [
    ...[...document.querySelectorAll('[ck-dayssince-spill]')].map((element) => ({ element })),
    ...[...document.querySelectorAll('[ck-dayssince-ticker]')].map((element) => ({
      element,
      suffix: TICKER_SUFFIX,
    })),
  ]

  const render = ({ element, suffix }: (typeof targets)[number]) => {
    const text = formatSince({
      iso: element.getAttribute('ck-dayssince-iso'),
      dateString: element.getAttribute('ck-dayssince'),
      suffix,
    })
    if (text && element.textContent !== text) element.textContent = text
  }

  targets.forEach(render)

  // Only hour-level entries go stale quickly. The ticker duplicates its items after this runs,
  // so re-query it on each pass to keep the decorative copies in step.
  const recent = targets.filter(({ element }) => isRecent(element.getAttribute('ck-dayssince-iso')))
  if (!recent.length) return
  setInterval(() => {
    recent.forEach(render)
    document
      .querySelectorAll('[ck-dayssince-ticker]')
      .forEach((element) => render({ element, suffix: TICKER_SUFFIX }))
  }, REFRESH_MS)
}
